import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {Select, SelectChangeEvent} from 'primeng/select';
import {DatePipe} from '@angular/common';
import {PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {ActivatedRoute, Router} from '@angular/router';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {MessageService} from 'primeng/api';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UserOverviewDto, UsersClient} from '../../api-client';
import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {UserLinkComponent} from '../../shared/components/user-link/user-link.component';
import {homeyStatusToTag, paymentStatusToTag} from '../../shared/enum-to-tag';

type View = 'all' | 'to-sync' | 'active' | 'mandatory-flow';

const fuzzinessOptions = [
  {label: 'Exact', value: 0},
  {label: 'Low', value: 0.2},
  {label: 'Medium', value: 0.4},
  {label: 'High', value: 0.6},
] as const;

const viewOptions = [
  {label: 'All', value: 'all'},
  {label: 'To be synced soon', value: 'to-sync'},
  {label: 'Active', value: 'active'},
  {label: 'Mandatory Flow', value: 'mandatory-flow'},
] as const;

@Component({
  selector: 'homey-users-page',
  standalone: true,
  imports: [
    FormsModule,
    Select,
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    UserLinkComponent,
    InputText,
    Button,
    Tooltip,
  ],
  templateUrl: './users-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersPageComponent implements OnInit {
  private readonly usersClient = inject(UsersClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);

  protected readonly users = signal<UserOverviewDto[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly loading = signal(false);

  protected readonly emailSearch = signal('');
  protected readonly emailSearchLoading = signal(false);

  protected readonly phoneSearch = signal('');
  protected readonly phoneSearchLoading = signal(false);

  public readonly view = input<View>('all');
  protected readonly finalView = computed(() => this.view() ?? 'all');

  public readonly search = input('');
  protected readonly fuzziness = signal(0.4);
  protected readonly fuzzinessOptions = [...fuzzinessOptions];
  protected readonly viewOptions = [...viewOptions];

  private readonly searchTrigger$ = new Subject<{search: string; view: View; fuzziness: number}>();

  constructor() {
    this.searchTrigger$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.search === b.search && a.view === b.view && a.fuzziness === b.fuzziness),
        tap(() => this.loading.set(true)),
        switchMap(({search, view, fuzziness}) =>
          this.usersClient.searchUsers(
            search,
            view === 'all' ? undefined : view,
            fuzziness.toString()
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (result) => {
          this.users.set(result.users);
          this.totalCount.set(result.totalCount);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  public ngOnInit(): void {
    this.triggerSearch(this.search(), this.finalView(), this.fuzziness());
  }

  protected onModeChange(event: SelectChangeEvent): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {view: event.value},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.triggerSearch(this.search(), event.value, this.fuzziness());
  }

  protected onSearchChange(value: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {search: value},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.triggerSearch(value, this.finalView(), this.fuzziness());
  }

  protected onFuzzinessChange(value: number): void {
    this.fuzziness.set(value);
    this.triggerSearch(this.search(), this.finalView(), value);
  }

  private triggerSearch(search: string | undefined, view: View | undefined, fuzziness: number): void {
    this.searchTrigger$.next({search: search ?? '', view: view ?? 'all', fuzziness});
  }

  protected openById(): void {
    const enteredId = prompt('Enter the user ID to open:');
    const trimmedId = enteredId?.trim();

    if (trimmedId) {
      this.router.navigate(['/users', trimmedId]);
    }
  }

  protected searchByEmail(): void {
    const email = this.emailSearch().trim();
    if (!email) return;

    this.emailSearchLoading.set(true);
    this.usersClient.findUserByEmail(email).subscribe({
      next: (users) => {
        this.emailSearchLoading.set(false);
        const match = users[0];
        if (match) {
          this.router.navigate(['/users', match.id]);
        } else {
          this.messageService.add({severity: 'warn', summary: 'No user found', detail: `No user with email ${email}`});
        }
      },
      error: () => {
        this.emailSearchLoading.set(false);
      },
    });
  }

  protected searchByPhone(): void {
    const phone = this.phoneSearch().trim();
    if (!phone) return;

    this.phoneSearchLoading.set(true);
    this.usersClient.findUserByPhone(phone).subscribe({
      next: ({userId}) => {
        this.phoneSearchLoading.set(false);
        this.router.navigate(['/users', userId]);
      },
      error: (err: unknown) => {
        this.phoneSearchLoading.set(false);
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this.messageService.add({severity: 'warn', summary: 'No user found', detail: `No user with phone number ${phone}`});
        }
      },
    });
  }

  protected readonly homeyStatusToTag = homeyStatusToTag;
  protected readonly paymentStatusToTag = paymentStatusToTag;
}
